import { useApolloClient } from '@apollo/client';
import { first, isEmpty, isNil } from 'lodash';
import { useCallback, useState } from 'react';
import { useAlert } from '@shared/components/Modal';
import { useValidateUploads } from '@shared/scenes/upload/hooks';
import { DEFAULT_MIME_TYPE, uploadFile } from '@shared/scenes/upload/store/actions';
import { BLOCKED_CONTENT_TYPES, MBUploadUtil, MBUploadUtilFile } from '@shared/scenes/upload/util';
import { toBrowserDecodableImage } from '@shared/scenes/upload/util/decodableImage';
import { captureVideoPoster } from '@shared/scenes/upload/util/videoPoster';
import { FileUpload } from '@shared/types/fieldValues';
import { i18n } from '@shared/util/i18n';
import { isVideoContentType } from '@shared/util/media/type';

export interface UseFileUploadOptions {
  onFileAdded(files: FileUpload[]): void;
  onFileRemoved(file: FileUpload): void;
  acceptedContentTypes?: string[];
  multiple?: boolean;
  /**
   * Uploads a still alongside each video and returns it as `posterUrl`, so a thumbnail can be
   * shown without touching the video. Off by default: it costs a decode and a second upload,
   * and only callers that render video thumbnails need it.
   */
  capturePoster?: boolean;
}

export interface UseFileUploadResult {
  handleUploadFile(files: MBUploadUtilFile[]): void;
  handleDrop(file: File[]): void;
  handleClear(file: FileUpload): void;
  showFilePicker(): void;
  uploading: boolean;
}

/**
 * Uploads a still for a video, or returns undefined — for a non-video, when the caller did not
 * ask, or when the frame could not be taken. A missing poster is never worth failing an upload
 * over; the thumbnail just falls back.
 */
async function uploadPoster(
  client: ReturnType<typeof useApolloClient>,
  file: MBUploadUtilFile,
  capturePoster: boolean,
): Promise<string | undefined> {
  if (!capturePoster || !isVideoContentType(file.contentType)) {
    return undefined;
  }

  let poster: MBUploadUtilFile | null = null;
  try {
    poster = await captureVideoPoster(file);
    return poster ? await uploadFile(client, poster) : undefined;
  } catch (err) {
    console.error(err);
    return undefined;
  } finally {
    // The still only ever existed to be uploaded, so its object URL is released either way —
    // nothing renders from it, and the browser holds the blob until it is.
    if (poster) {
      URL.revokeObjectURL(poster.url);
    }
  }
}

export function useFileUpload({
  onFileAdded,
  onFileRemoved,
  acceptedContentTypes,
  multiple = true,
  capturePoster = false,
}: UseFileUploadOptions): UseFileUploadResult {
  const client = useApolloClient();
  const [uploading, setUploading] = useState(false);
  const alert = useAlert();

  const validateUploads = useValidateUploads({
    showUpgradeModal: true,
  });

  const handleUploadFile = useCallback(
    async (files: MBUploadUtilFile[]) => {
      setUploading(true);

      try {
        const uploadedFiles: FileUpload[] = [];
        for (const file of files) {
          const isFileValid = !!validateUploads([file]);
          if (!isFileValid) {
            continue;
          }

          const url = await uploadFile(client, file);
          uploadedFiles.push({
            url,
            name: file.name,
            contentType: file.contentType || DEFAULT_MIME_TYPE,
            width: file.width,
            height: file.height,
            posterUrl: await uploadPoster(client, file, capturePoster),
          });
        }

        if (multiple || !isEmpty(uploadedFiles)) {
          onFileAdded(uploadedFiles);
        }
      } catch (err) {
        console.error(err);
        alert({
          message: i18n('card.blocks.image.uploadFailed'),
        });
      } finally {
        setUploading(false);
      }
    },
    [client, multiple, onFileAdded, validateUploads, alert, capturePoster],
  );

  const showFilePicker = useCallback(async () => {
    const files = await MBUploadUtil.selectFiles({ multiple, acceptedContentTypes });
    if (multiple) {
      if (!isNil(files) && files.length > 0) {
        handleUploadFile(files);
      }
      return;
    }

    const firstFile = first(files);
    if (!isNil(firstFile)) {
      handleUploadFile([firstFile]);
    }
  }, [acceptedContentTypes, handleUploadFile, multiple]);

  const handleClear = useCallback((file: FileUpload) => onFileRemoved(file), [onFileRemoved]);

  const handleDrop = useCallback(
    async (files: File[]) => {
      const safeFiles = files?.filter(f => !BLOCKED_CONTENT_TYPES.includes(f.type));
      if (!safeFiles?.length) {
        return;
      }

      const filesToUpload: MBUploadUtilFile[] = [];
      const dropTargets = multiple ? safeFiles : [safeFiles[0]];

      for (const droppedFile of dropTargets) {
        const file = await toBrowserDecodableImage(droppedFile);
        const dims = await MBUploadUtil.getWebFileDimensions(file);
        filesToUpload.push({
          id: String(Date.now()),
          url: URL.createObjectURL(file),
          name: file.name,
          contentType: file.type,
          size: file.size,
          ...(dims ?? {}),
        });
      }

      handleUploadFile(filesToUpload);
    },
    [handleUploadFile, multiple],
  );

  return {
    uploading,
    handleUploadFile,
    handleDrop,
    handleClear,
    showFilePicker,
  };
}
